<template>
  <div
    class="lpu-schedule-slots"
    :class="isMobile
      ? 'lpu-schedule-slots--mobile'
      : 'lpu-schedule-slots--desktop'"
  >
    <button
      v-for="(slot, index) in visibleSlots"
      :key="`${slot.time}-${slot.doctor_id}-${index}`"
      type="button"
      :data-qa="slotQa"
      data-google-goal="click_appointment_slot"
      class="lpu-schedule-slots__button text-subtitle-1"
      :class="{ 'lpu-schedule-slots__button--disabled': isDisabled }"
      :disabled="isDisabled"
      @click="onSlotClick($event, slot)"
    >
      {{ slot.time }}
    </button>

    <button
      v-if="hasExpandButton"
      type="button"
      :data-qa="expandQa"
      class="lpu-schedule-slots__expand"
      @click.stop="toggleExpanded"
    >
      <span
        class="ui-icon-fz-big"
        :class="[
          isExpanded ? 'ui-icon-arrow-up' : 'ui-icon-arrow-down',
          ff_lpu_branding_page_v5
            ? 'text-lpu-branding-color-text'
            : 'text-lpu-branding-color-primary',
        ]"
      />
    </button>
  </div>
</template>

<script lang="ts" setup>
/**
 * Сетка слотов времени календаря записи на странице клиники.
 *
 * `doctor_id` у слота опционален: у записи на услугу его нет.
 * Локаторы передаёт родитель, чтобы секции не делили одни `data-qa`.
 */
import { computed, ref, watch } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import type { ScheduleSlot } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/types'

const MOBILE_COLS = 4
const DESKTOP_COLS = 5
const ROWS = 2
const MOBILE_VISIBLE_SLOTS = MOBILE_COLS * ROWS
const DESKTOP_VISIBLE_SLOTS = DESKTOP_COLS * ROWS

type Emits = {
  (_event: 'click', _nativeEvent: Event, _time: string, _doctorId: number | undefined): void
}

const props = withDefaults(defineProps<{
  slots: ScheduleSlot[]
  isDisabled?: boolean
  slotQa: string
  expandQa: string
}>(), {
  isDisabled: false,
})

const emit = defineEmits<Emits>()

const { context }: CommonContextStore = useContextStore()
const ff_lpu_branding_page_v5 = computed(() => (
  Boolean(context.request.feature_flags.ff_lpu_branding_page_v5)
))

const isMobile = useIsMobile()
const isExpanded = ref(false)

const visibleSlotsLimit = computed(() => (
  isMobile.value ? MOBILE_VISIBLE_SLOTS : DESKTOP_VISIBLE_SLOTS
))

const hasExpandButton = computed(() => props.slots.length > visibleSlotsLimit.value)

const visibleSlots = computed(() => {
  if (!hasExpandButton.value || isExpanded.value) {
    return props.slots
  }

  // Одна ячейка сетки остаётся под кнопку expand
  return props.slots.slice(0, visibleSlotsLimit.value - 1)
})

watch(() => props.slots, () => {
  isExpanded.value = false
})

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
}

function onSlotClick(nativeEvent: Event, slot: ScheduleSlot) {
  if (props.isDisabled) {
    return
  }

  emit('click', nativeEvent, slot.time, slot.doctor_id)
}
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.lpu-schedule-slots {
  display: grid;
  gap: 8px;
  width: 100%;
  max-width: 100%;

  &--desktop {
    grid-template-columns: repeat(5, max-content);
  }

  &--mobile {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  &__button,
  &__expand {
    box-sizing: border-box;
    appearance: none;
    height: 40px;
    border: none;
    cursor: pointer;
    outline: none;
  }

  &__button {
    padding: 8px 4px;
    color: $ui-kit-bg-gray-0;
    background-color: var(--lpu-branding-color-primary, $ui-kit-primary);
    border-radius: 15px;
  }

  &__button--disabled {
    color: $ui-kit-text;
    background-color: $ui-kit-bg-gray-0;
    cursor: default;
  }

  &__expand {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 15px;
    background-color: var(--lpu-branding-color-background, $ui-kit-bg-primary);
  }

  &--desktop &__button,
  &--desktop &__expand {
    min-width: 70px;
  }

  &--mobile &__button,
  &--mobile &__expand {
    width: 100%;
    min-width: 0;
    border-radius: 12px;
  }
}
</style>
